import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'

interface ContextWindowBarProps {
  contextTokens: number
  contextWindow: number
  compacted: boolean
  /** Slim inline caption (no full-width strip / border) — sits cleanly under the chat header. */
  compact?: boolean
}

// Matches the backend COMPACTION_THRESHOLD — the point at which auto-compact triggers
const COMPACTION_THRESHOLD = 0.6

const ContextWindowBar: React.FC<ContextWindowBarProps> = ({contextTokens, contextWindow, compacted, compact = false}) => {
  const [showCompactedNotice, setShowCompactedNotice] = useState(false)

  useEffect(() => {
    if (compacted) {
      setShowCompactedNotice(true)
      const timer = setTimeout(() => setShowCompactedNotice(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [compacted])

  const compactionBudget = contextWindow * COMPACTION_THRESHOLD
  const headroom = Math.max(0, Math.round(((compactionBudget - contextTokens) / compactionBudget) * 100))

  const headroomColour =
    headroom > 60 ? 'success.main' :
    headroom > 30 ? 'warning.main' :
    'error.main'

  // Compact: a single slim, muted caption (no strip/border) — meant to sit just under the chat header.
  if (compact) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, pt: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          {contextTokens.toLocaleString()} tokens
          {showCompactedNotice ? (
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 500, ml: 1 }}>· Auto-compacted ✓</Box>
          ) : (
            <Box component="span" sx={{ color: headroomColour, ml: 1 }}>· {headroom}% context left</Box>
          )}
        </Typography>
      </Box>
    )
  }

  // Constrained to the same 820px content column as the chat header, message thread, and input
  // (outer full-width padding, inner centered column) so the strip doesn't span the whole pane.
  return (
    <Box sx={{ px: 3, flexShrink: 0 }}>
      <Box
        sx={{
          maxWidth: 820,
          mx: 'auto',
          minHeight: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {contextTokens.toLocaleString()} tokens used
        </Typography>

        {showCompactedNotice ? (
          <Typography variant="caption" sx={{color: 'warning.main', fontWeight: 500}}>
            Auto-compacted ✓
          </Typography>
        ) : (
          <Typography variant="caption" sx={{color: headroomColour}}>
            Context left until auto-compact: {headroom}%
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default ContextWindowBar
